import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { authService } from "../../services/authService";
import { fetchMe } from "../../store/slices/authSlice";
import Icon from "../../components/Common/Icon";
import { useToast } from "../../context/ToastContext";

/**
 * Profil > Kasa Ekranı Şifresi. Login şifresiyle yetkilendirip kasa ekranı
 * erişim şifresini belirler/değiştirir. Yalnız MANAGER kullanır.
 */
export default function CashPasswordPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const user = useSelector((state) => state.auth.user);
  const hasCash = user?.hasCashPassword;

  const [loginPassword, setLoginPassword] = useState("");
  const [newCashPassword, setNewCashPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newCashPassword.length < 4) {
      setError("Kasa şifresi en az 4 karakter olmalı");
      return;
    }
    if (newCashPassword !== confirm) {
      setError("Yeni kasa şifreleri eşleşmiyor");
      return;
    }
    setLoading(true);
    try {
      await authService.setCashPassword({ loginPassword, newCashPassword });
      // Kilidi tazele: yeni şifre belirlendi, oturum kilidini sıfırla
      sessionStorage.removeItem("cashUnlocked");
      await dispatch(fetchMe());
      showToast("Kasa şifresi güncellendi.", "success");
      navigate("/cash");
    } catch (err) {
      const msg = err.response?.data?.error ?? "İşlem başarısız.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant p-8">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
          <Icon name="lock" className="!text-[28px]" />
        </div>
        <h1 className="text-2xl font-bold text-on-surface">Kasa Ekranı Şifresi</h1>
        <p className="text-on-surface-variant text-sm mt-1">
          {hasCash
            ? "Kasa ekranını açmak için kullanılan şifreyi değiştirin."
            : "Kasa ekranını korumak için bir erişim şifresi belirleyin. Bu şifre, hesap giriş şifrenizden ayrıdır."}
        </p>

        {error && (
          <div className="mt-4 flex items-center gap-2 text-sm text-error bg-error/10 rounded-xl px-4 py-3">
            <Icon name="error" className="!text-[18px]" /> {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <PwdField label="Hesap (giriş) şifreniz" value={loginPassword} onChange={setLoginPassword} />
          <PwdField label="Yeni kasa şifresi" value={newCashPassword} onChange={setNewCashPassword} />
          <PwdField label="Yeni kasa şifresi (tekrar)" value={confirm} onChange={setConfirm} />
          <p className="text-xs text-on-surface-variant">En az 4 karakter.</p>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition active:scale-[0.99] disabled:opacity-50"
          >
            {loading ? "Kaydediliyor…" : hasCash ? "Kasa Şifresini Değiştir" : "Kasa Şifresi Belirle"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PwdField({ label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-outline-variant rounded-xl pl-3.5 pr-11 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition bg-white"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-3.5 top-3 text-on-surface-variant hover:text-primary transition"
        >
          <Icon name={show ? "visibility_off" : "visibility"} className="!text-[20px]" />
        </button>
      </div>
    </div>
  );
}
